import { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import InsSection from '../components/ins/InsSection'
import AddressAutocomplete from '../components/ui/AddressAutocomplete'
import type { InsStatut } from '../types'
import { patientsService } from '../services/api/patients'
import { useDeletePatient } from '../hooks/usePatients'
import toast from 'react-hot-toast'

interface PatientFormData {
  firstName: string
  lastName: string
  birthDate: string
  phone: string
  email: string
  age: string
  height: string
  weight: string
  adresse: string
  disease: string
  medications: string
  treatment: string
  symptoms: string
  observations: string
  insStatut?: InsStatut
  insMatricule?: string
}

const EMPTY_FORM: PatientFormData = {
  firstName: '',
  lastName: '',
  birthDate: '',
  phone: '',
  email: '',
  age: '',
  height: '',
  weight: '',
  adresse: '',
  disease: '',
  medications: '',
  treatment: '',
  symptoms: '',
  observations: '',
}

function computeBmi(height: string, weight: string): { value: number; label: string; color: string } | null {
  const h = parseFloat(height)
  const w = parseFloat(weight)
  if (!h || !w || h <= 0 || w <= 0) return null
  const hm = h / 100
  const bmi = w / (hm * hm)
  if (bmi < 18.5) return { value: bmi, label: 'Insuffisance pondérale', color: 'text-yellow-600 bg-yellow-50 border-yellow-200 dark:bg-yellow-500/10 dark:border-yellow-500/30 dark:text-yellow-400' }
  if (bmi < 25) return { value: bmi, label: 'Poids normal', color: 'text-green-600 bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-400' }
  if (bmi < 30) return { value: bmi, label: 'Surpoids', color: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-400' }
  return { value: bmi, label: 'Obésité', color: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-400' }
}

export default function PatientDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const patientId = id && id !== 'new' ? parseInt(id) : 0
  const isNew = patientId === 0

  const [form, setForm] = useState<PatientFormData>(EMPTY_FORM)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const deletePatient = useDeletePatient()

  const bmi = useMemo(() => computeBmi(form.height, form.weight), [form.height, form.weight])

  const loadPatient = async () => {
    if (isNew) return
    setLoading(true)
    setError(null)
    try {
      const res = await patientsService.getById(patientId)
      const p = res.data
      if (!p) throw new Error('Patient introuvable')
      setForm({
        firstName: p.firstName ?? '',
        lastName: p.lastName ?? '',
        birthDate: p.birthDate ?? '',
        phone: p.phone ?? '',
        email: p.email ?? '',
        age: p.age != null ? String(p.age) : '',
        height: p.height != null ? String(p.height) : '',
        weight: p.weight != null ? String(p.weight) : '',
        adresse: p.adresse ?? '',
        disease: p.disease ?? '',
        medications: p.medications ?? '',
        treatment: p.treatment ?? '',
        symptoms: p.symptoms ?? '',
        observations: p.observations ?? '',
        insStatut: p.insStatut,
        insMatricule: p.insMatricule,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void loadPatient() }, [patientId])

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Le prénom et le nom sont obligatoires')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        birthDate: form.birthDate || undefined,
        phone: form.phone || undefined,
        email: form.email || undefined,
        age: form.age ? parseInt(form.age) : undefined,
        height: form.height ? parseFloat(form.height) : undefined,
        weight: form.weight ? parseFloat(form.weight) : undefined,
        adresse: form.adresse || undefined,
        disease: form.disease || undefined,
        medications: form.medications || undefined,
        treatment: form.treatment || undefined,
        symptoms: form.symptoms || undefined,
        observations: form.observations || undefined,
      }
      if (isNew) {
        await patientsService.create(payload)
        toast.success('Patient créé')
      } else {
        await patientsService.update(patientId, payload)
        toast.success('Patient mis à jour')
      }
      await queryClient.invalidateQueries({ queryKey: ['patients'] })
      navigate('/patients')
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (key: keyof PatientFormData, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const handleDelete = async () => {
    if (isNew) return
    const name = `${form.firstName} ${form.lastName}`.trim()
    if (!window.confirm(`Supprimer définitivement le patient ${name} ?`)) return
    try {
      await deletePatient.mutateAsync(patientId)
      navigate('/patients', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression')
    }
  }

  if (loading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-gray-500 dark:text-gray-400'>Chargement...</div>
      </div>
    )
  }

  return (
    <div className='p-6 max-w-2xl mx-auto space-y-6'>
      <div className='flex items-center gap-3'>
        <button
          onClick={() => navigate('/patients')}
          className='text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
        >
          ← Retour
        </button>
        <h1 className='text-xl font-bold text-gray-900 dark:text-white'>
          {isNew ? 'Nouveau Patient' : 'Dossier Patient'}
        </h1>
      </div>

      {error && (
        <div className='bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-600 dark:text-red-400 rounded-xl px-4 py-3 text-sm'>
          {error}
        </div>
      )}

      {/* Informations personnelles */}
      <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4'>
        <h2 className='text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'>Informations personnelles</h2>

        {([
          { key: 'firstName', label: 'Prénom', type: 'text', required: true, placeholder: 'Entrez le prénom' },
          { key: 'lastName', label: 'Nom', type: 'text', required: true, placeholder: 'Entrez le nom' },
          { key: 'birthDate', label: 'Date de naissance', type: 'date', required: false, placeholder: '' },
          { key: 'phone', label: 'Téléphone', type: 'tel', required: false, placeholder: 'Entrez le numéro de téléphone' },
          { key: 'email', label: 'Email', type: 'email', required: false, placeholder: "Entrez l'adresse email" },
          { key: 'age', label: 'Âge', type: 'number', required: false, placeholder: 'Âge en années' },
        ] as { key: keyof PatientFormData; label: string; type: string; required: boolean; placeholder: string }[]).map(field => (
          <div key={field.key}>
            <label htmlFor={`patient-${field.key}`} className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {field.label}
              {field.required && <span className='text-red-500 ml-1'>*</span>}
            </label>
            <input
              id={`patient-${field.key}`}
              type={field.type}
              className='w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm'
              placeholder={field.placeholder}
              value={String(form[field.key] ?? '')}
              onChange={e => handleChange(field.key, e.target.value)}
              required={field.required}
            />
          </div>
        ))}

        <AddressAutocomplete
          id='patient-adresse'
          label='Adresse'
          value={form.adresse}
          onChange={val => handleChange('adresse', val)}
          placeholder='14 rue de Zurich, Strasbourg...'
        />
      </div>

      {/* IMC */}
      <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4'>
        <h2 className='text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'>Mesures corporelles</h2>
        <div className='grid grid-cols-2 gap-4'>
          <div>
            <label htmlFor='patient-height' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>Taille (cm)</label>
            <input
              id='patient-height'
              type='number'
              min='50'
              max='250'
              className='w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm'
              placeholder='ex: 175'
              value={form.height}
              onChange={e => handleChange('height', e.target.value)}
            />
          </div>
          <div>
            <label htmlFor='patient-weight' className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>Poids (kg)</label>
            <input
              id='patient-weight'
              type='number'
              min='1'
              max='500'
              className='w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm'
              placeholder='ex: 70'
              value={form.weight}
              onChange={e => handleChange('weight', e.target.value)}
            />
          </div>
        </div>
        {bmi && (
          <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 ${bmi.color}`}>
            <span className='text-2xl font-bold'>{bmi.value.toFixed(1)}</span>
            <div>
              <p className='text-xs font-semibold uppercase tracking-wide'>IMC</p>
              <p className='text-sm font-medium'>{bmi.label}</p>
            </div>
          </div>
        )}
      </div>

      {/* Informations médicales */}
      <div className='bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 space-y-4'>
        <h2 className='text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider'>Informations médicales</h2>

        {([
          { key: 'disease', label: 'Pathologie / Maladie', placeholder: 'Ex: Diabète de type 2' },
          { key: 'symptoms', label: 'Symptômes', placeholder: 'Décrire les symptômes observés' },
          { key: 'medications', label: 'Médicaments', placeholder: 'Liste des médicaments prescrits' },
          { key: 'treatment', label: 'Traitement', placeholder: 'Plan de traitement en cours' },
          { key: 'observations', label: 'Observations', placeholder: 'Notes et observations infirmières' },
        ] as { key: keyof PatientFormData; label: string; placeholder: string }[]).map(field => (
          <div key={field.key}>
            <label htmlFor={`patient-${field.key}`} className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
              {field.label}
            </label>
            <textarea
              id={`patient-${field.key}`}
              rows={2}
              className='w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-dark-100 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-all text-sm resize-none'
              placeholder={field.placeholder}
              value={String(form[field.key] ?? '')}
              onChange={e => handleChange(field.key, e.target.value)}
            />
          </div>
        ))}
      </div>

      <button
        onClick={() => void handleSave()}
        disabled={saving || deletePatient.isPending}
        className='w-full bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors text-sm'
      >
        {saving ? 'Enregistrement...' : isNew ? 'Créer le patient' : 'Enregistrer les modifications'}
      </button>

      {!isNew && (
        <button
          type='button'
          onClick={() => void handleDelete()}
          disabled={saving || deletePatient.isPending}
          className='w-full border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 hover:bg-red-100 dark:hover:bg-red-500/20 disabled:opacity-50 text-red-600 dark:text-red-400 font-semibold py-2.5 rounded-xl transition-colors text-sm'
        >
          {deletePatient.isPending ? 'Suppression...' : 'Supprimer le patient'}
        </button>
      )}

      {!isNew && (
        <InsSection
          patientId={patientId}
          insStatut={form.insStatut}
          insMatricule={form.insMatricule}
          onUpdated={() => void loadPatient()}
        />
      )}
    </div>
  )
}
